import { stringify } from "qs";

import { AWSFeatureName, type CodeSnippet } from "../../types";
import {
  DEFAULT_ROLE_NAME,
  NEW_STACK_NAME,
  REAL_TIME_DATA_SNS_DEV_ACCOUNT_ID,
  REAL_TIME_DATA_SNS_PROD_ACCOUNT_ID,
  SNS_ARN,
  UPDATE_STACK_NAME,
} from "./const";
import { type CloudConnectCLIParameters } from "./types";

const isProd = process.env.REACT_APP_FIREBASE_PROJECT_ID === "me-doit-intl-com";

const AMAZON_TEMPLATE_URL_PREFIX = `https://doit-cmp-ops-pub.s3.us-east-1.amazonaws.com/${isProd ? "prod" : "dev"}`;
const UPDATE_TEMPLATE_URL = `${AMAZON_TEMPLATE_URL_PREFIX}/cmp_create_policies.yml`;
const UPDATE_TEMPLATE_WITH_FSK8S_URL = `${AMAZON_TEMPLATE_URL_PREFIX}/cmp_create_policies_fsk8s.yml`;
const CREATE_TEMPLATE_URL = `${AMAZON_TEMPLATE_URL_PREFIX}/cmp_create_role_N_policies.yml`;
const CREATE_TEMPLATE_WITH_FSK8S_URL = `${AMAZON_TEMPLATE_URL_PREFIX}/cmp_create_role_N_policies_fsk8s.yml`;

const UPDATE_LAMBDA_PREFIX = `${location.origin}/webhooks/v1/aws`;
const UPDATE_ROLE_LAMBDA_URL = `${UPDATE_LAMBDA_PREFIX}/updateAccountRole`;
const UPDATE_FEATURE_LAMBDA_URL = `${UPDATE_LAMBDA_PREFIX}/updateAwsFeature`;

const REAL_TIME_DATA_SNS_ACCOUNT_ID = isProd ? REAL_TIME_DATA_SNS_PROD_ACCOUNT_ID : REAL_TIME_DATA_SNS_DEV_ACCOUNT_ID;

const generateUrl = (params: any) => {
  const paramsStr = stringify(params);
  return `https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/quickcreate?${paramsStr}`;
};

const addTimeStamp = (str: string) => `${str}-${Date.now()}`;

export const getQuickCreateUrl = (
  customerId: string,
  selectedFeatures: string[],
  isFSK8SFeatureEnabled: boolean,
  realTimeDataBucketName: string
) => {
  const stackName = addTimeStamp(NEW_STACK_NAME);
  const params = {
    templateUrl: isFSK8SFeatureEnabled ? CREATE_TEMPLATE_WITH_FSK8S_URL : CREATE_TEMPLATE_URL,
    stackName,
    param_NotificationUrl: UPDATE_ROLE_LAMBDA_URL,
    param_SnsArn: SNS_ARN,
    param_RoleToAddPolicyTo: DEFAULT_ROLE_NAME,
    param_ExternalId: customerId,
    param_SpotScaling: selectedFeatures.includes(AWSFeatureName.spotScaling),
    param_Quotas: selectedFeatures.includes(AWSFeatureName.quotas),
    param_FSK8SCore: selectedFeatures.includes(AWSFeatureName.fsk8s_core),
    param_FSK8SClusterConnect: selectedFeatures.includes(AWSFeatureName.fsk8s_auto_connect),
    param_RealTimeData: selectedFeatures.includes(AWSFeatureName.realTimeData),
    param_RealTimeDataBucketName: realTimeDataBucketName,
    param_RealTimeDataSNSAccountId: REAL_TIME_DATA_SNS_ACCOUNT_ID,
    param_TrustedAdvisorInsights: selectedFeatures.includes(AWSFeatureName.trustedAdvisorInsights),
    param_CostOptimizationHubInsights: selectedFeatures.includes(AWSFeatureName.costOptimizationHubInsights),
    param_CloudDiagrams: selectedFeatures.includes(AWSFeatureName.cloud_diagrams),
    param_SecurityHubInsights: selectedFeatures.includes(AWSFeatureName.securityHubInsights),
  };
  return [generateUrl(params), stackName];
};

export const getQuickUpdateUrl = (
  customerId: string,
  roleName: string,
  selectedFeatures: string[],
  isFSK8SFeatureEnabled: boolean,
  realTimeDataBucketName: string
) => {
  const stackName = addTimeStamp(UPDATE_STACK_NAME);
  const params = {
    templateUrl: isFSK8SFeatureEnabled ? UPDATE_TEMPLATE_WITH_FSK8S_URL : UPDATE_TEMPLATE_URL,
    stackName,
    param_NotificationUrl: UPDATE_FEATURE_LAMBDA_URL,
    param_SnsArn: SNS_ARN,
    param_RoleToAddPolicyTo: roleName,
    param_ExternalId: customerId,
    param_SpotScaling: selectedFeatures.includes(AWSFeatureName.spotScaling),
    param_Quotas: selectedFeatures.includes(AWSFeatureName.quotas),
    param_FSK8SCore: selectedFeatures.includes(AWSFeatureName.fsk8s_core),
    param_FSK8SClusterConnect: selectedFeatures.includes(AWSFeatureName.fsk8s_auto_connect),
    param_RealTimeData: selectedFeatures.includes(AWSFeatureName.realTimeData),
    param_RealTimeDataBucketName: realTimeDataBucketName,
    param_TrustedAdvisorInsights: selectedFeatures.includes(AWSFeatureName.trustedAdvisorInsights),
    param_CostOptimizationHubInsights: selectedFeatures.includes(AWSFeatureName.costOptimizationHubInsights),
    param_CloudDiagrams: selectedFeatures.includes(AWSFeatureName.cloud_diagrams),
    param_SecurityHubInsights: selectedFeatures.includes(AWSFeatureName.securityHubInsights),
  };

  return [generateUrl(params), stackName];
};

const generateCLICommand = (stackName: string, templateUrl: string, params: CloudConnectCLIParameters) => {
  const paramsList: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    paramsList.push(`ParameterKey=${key},ParameterValue=${value}`);
  }

  return `aws cloudformation create-stack --stack-name ${stackName} --template-url ${templateUrl} --parameters ${paramsList.join(
    " "
  )}  --region us-east-1 --capabilities CAPABILITY_NAMED_IAM`;
};

export const getCLICreateCommand = (
  customerId: string,
  selectedFeatures: string[],
  isFSK8SFeatureEnabled: boolean,
  realTimeDataBucketName: string
) => {
  const stackName = addTimeStamp(NEW_STACK_NAME);
  const params: CloudConnectCLIParameters = {
    ExternalId: customerId,
    RoleToAddPolicyTo: DEFAULT_ROLE_NAME,
    SpotScaling: selectedFeatures.includes(AWSFeatureName.spotScaling),
    Quotas: selectedFeatures.includes(AWSFeatureName.quotas),
    RealTimeData: selectedFeatures.includes(AWSFeatureName.realTimeData),
    RealTimeDataBucketName: realTimeDataBucketName,
    TrustedAdvisorInsights: selectedFeatures.includes(AWSFeatureName.trustedAdvisorInsights),
    CloudDiagrams: selectedFeatures.includes(AWSFeatureName.cloud_diagrams),
    SecurityHubInsights: selectedFeatures.includes(AWSFeatureName.securityHubInsights),
  };

  if (isFSK8SFeatureEnabled) {
    params.FSK8SCore = selectedFeatures.includes(AWSFeatureName.fsk8s_core);
    params.FSK8SClusterConnect = selectedFeatures.includes(AWSFeatureName.fsk8s_auto_connect);
  }

  if (!isProd) {
    params.SnsArn = SNS_ARN;
    params.NotificationUrl = UPDATE_ROLE_LAMBDA_URL;
  }

  return [
    generateCLICommand(stackName, isFSK8SFeatureEnabled ? CREATE_TEMPLATE_WITH_FSK8S_URL : CREATE_TEMPLATE_URL, params),
    stackName,
  ];
};

export const getRealTimeDataCommand = (bucketName: string) =>
  `aws s3api put-bucket-notification-configuration \\
    --bucket=${bucketName} \\
    --notification-configuration file://notification_configuration.json`;

export const getCLIUpdateCommand = (
  customerId: string,
  roleName: string,
  selectedFeatures: string[],
  isFSK8SFeatureEnabled: boolean,
  realTimeDataBucketName: string
) => {
  const stackName = addTimeStamp(UPDATE_STACK_NAME);
  const params: CloudConnectCLIParameters = {
    ExternalId: customerId,
    RoleToAddPolicyTo: roleName,
    SpotScaling: selectedFeatures.includes(AWSFeatureName.spotScaling),
    Quotas: selectedFeatures.includes(AWSFeatureName.quotas),
    RealTimeData: selectedFeatures.includes(AWSFeatureName.realTimeData),
    RealTimeDataBucketName: realTimeDataBucketName,
    TrustedAdvisorInsights: selectedFeatures.includes(AWSFeatureName.trustedAdvisorInsights),
    CloudDiagrams: selectedFeatures.includes(AWSFeatureName.cloud_diagrams),
    SecurityHubInsights: selectedFeatures.includes(AWSFeatureName.securityHubInsights),
  };

  if (isFSK8SFeatureEnabled) {
    params.FSK8SCore = selectedFeatures.includes(AWSFeatureName.fsk8s_core);
    params.FSK8SClusterConnect = selectedFeatures.includes(AWSFeatureName.fsk8s_auto_connect);
  }

  if (!isProd) {
    params.NotificationUrl = UPDATE_FEATURE_LAMBDA_URL;
  }

  return [
    generateCLICommand(stackName, isFSK8SFeatureEnabled ? UPDATE_TEMPLATE_WITH_FSK8S_URL : UPDATE_TEMPLATE_URL, params),
    stackName,
  ];
};

export const getNotificationConfigurationSnippet = (isLocalhost: boolean): CodeSnippet => {
  const topicAccount = isLocalhost ? REAL_TIME_DATA_SNS_DEV_ACCOUNT_ID : REAL_TIME_DATA_SNS_PROD_ACCOUNT_ID;

  const code = `{
    "TopicConfigurations": [
      {
        "TopicArn": "arn:aws:sns:us-east-1:${topicAccount}:realtime-event-topic-trigger",
        "Events": [
          "s3:ObjectCreated:*"
        ]
      }
    ]
  }`;

  const name = "notification_configuration.json";

  return {
    code,
    name,
  };
};
